<template>
  <Window
    class="cc-drilldown-window"
    :title="title"
    :modal="true"
    :width="windowWidth"
    :height="windowHeight"
    @close="$emit('close')"
  >
    <div class="cc-drilldown-content">
      <div v-if="loading" class="cc-drilldown-loading">
        <Loader />
      </div>
      <div v-else-if="error" class="cc-drilldown-error">
        {{ error }}
      </div>
      <Grid
        v-else
        :data-items="rows"
        :columns="columns"
        :resizable="true"
        :sortable="true"
      />
    </div>
  </Window>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { Grid } from '@progress/kendo-vue-grid'
import { Window } from '@progress/kendo-vue-dialogs'
import { Loader } from '@progress/kendo-vue-indicators'
import { useI18n } from 'vue-i18n'
import { PwsDynamicReportGet, PwsDynamicReportExecute } from '@/api/ConsultantCorner'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'

const { t } = useI18n()

interface Props {
  // Child report id, conventionally `${parentReportId}_${columnName}`.
  reportId: string
  // Localized dialog heading (the clicked column's localized name).
  title: string
  // Consultant to scope the report to; authorized server-side against the
  // caller's downline. Null runs it for the caller.
  beeNumberOverride?: number | null
  // Server-side filters carried over from the parent report (e.g. the chosen
  // batch/period) so the drill-down reconciles with the count that was clicked.
  filters?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  beeNumberOverride: null,
  filters: () => []
})

defineEmits<{ close: [] }>()

const loading = ref(true)
const error = ref('')
const rows = ref<any[]>([])
const columns = ref<any[]>([])

const windowWidth = ref(900)
const windowHeight = ref(600)

onMounted(() => {
  windowWidth.value = Math.min(900, window.innerWidth - 40)
  windowHeight.value = Math.min(600, window.innerHeight - 40)
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const definition = await PwsDynamicReportGet(props.reportId)

    const visibleColumns = definition.item.columns
      .filter(col => col.columnName !== 'ParentBeeNumber' && !col.isFilterOnly)
      .sort((a, b) => a.displayOrder - b.displayOrder)

    columns.value = visibleColumns.map(col => ({
      field: col.columnName,
      title: col.localizedColumnName,
      width: `${resolveColumnWidth(col.minWidth, col.localizedColumnName)}px`
    }))

    // Start from the child report's own default filters, then let the parent's
    // server filters override per column (same merge the main report uses).
    let allFilters = [...(definition.item.filters || [])]
    ;(props.filters || []).forEach(parentFilter => {
      allFilters = allFilters.filter(f => f.columnName !== parentFilter.columnName)
      allFilters.push(parentFilter)
    })

    const result = await PwsDynamicReportExecute({
      reportId: props.reportId,
      ColumnNames: visibleColumns.map(col => col.columnName),
      Filters: allFilters,
      BeeNumberOverride: props.beeNumberOverride ?? undefined
    })

    rows.value = result.Items || []
  } catch (err: any) {
    error.value = err?.message || t('ccReport.DrilldownLoadError')
  } finally {
    loading.value = false
  }
}

// Reload when the target column/consultant changes while the dialog is open.
watch(
  () => [props.reportId, props.beeNumberOverride],
  () => load(),
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.cc-drilldown-content {
  min-height: 200px;
}

.cc-drilldown-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.cc-drilldown-error {
  color: red;
  padding: $card-padding;
  text-align: center;
}
</style>
